<script setup lang="ts">
	import StringMethods from '~/utils/character'
	import adminAvatar from '@/assets/images/jpg/memoji.jpg'

	export type AdminLabelSchema = {
		value: string
		iconDir: string
		iconName: string
	}
	export type AdminProfileSchema = {
		first_name?: AdminLabelSchema
		last_name?: AdminLabelSchema
		role?: AdminLabelSchema
		gender?: AdminLabelSchema & {
			value: 'male' | 'female' | 'N/a'
			iconName: string
		}
		house_address?: AdminLabelSchema
		state?: AdminLabelSchema
		phone?: AdminLabelSchema
		email?: AdminLabelSchema
		city?: AdminLabelSchema
	}
	const { accountInfo } = useAuth()
	const userInfo = computed(() => {
		return accountInfo.value
	})
	const profile = computed(() => ({
		first_name: {
			value: accountInfo.value?.first_name || 'N/a',
			iconDir: 'line',
			iconName: 'person'
		},
		last_name: {
			value: accountInfo.value?.last_name || 'N/a',
			iconDir: 'line',
			iconName: 'person'
		},
		email: {
			value: accountInfo.value?.email || 'N/a',
			iconDir: 'communication',
			iconName: 'mail'
		},
		phone: {
			value: accountInfo.value?.phone || 'N/a',
			iconDir: 'communication',
			iconName: 'phone'
		},
		gender: {
			value: accountInfo.value?.gender || 'N/a',
			iconDir: 'line',
			iconName: 'gender'
		},
		house_address: {
			value: accountInfo.value?.address || 'N/a',
			iconDir: 'line',
			iconName: 'location'
		},
		city: {
			value: accountInfo.value?.city || 'N/a',
			iconDir: 'line',
			iconName: 'buildings'
		},
		state: {
			value: accountInfo.value?.state || 'N/a',
			iconDir: 'line',
			iconName: 'routing'
		}
	}))

	const profileCardInformation = computed(() => {
		const personal = profile.value
		return {
			first_name: personal?.first_name?.value || '',
			last_name: personal?.last_name?.value || '',
			email: personal?.email?.value,
			phone: personal?.phone?.value,
			gender: personal?.gender?.value,
			house_address: personal?.house_address?.value
		}
	})
	const username = computed(() => {
		return `${userInfo.value?.first_name || 'N'} ${userInfo.value?.last_name || '/a'}`
	})

	const { stripUnderScore } = StringMethods()
</script>

<template>
	<section class="w-full justify-between gap-6 md:gap-y-0 lg:flex">
		<div class="w-full space-y-6">
			<shared-card
				wrapper-class="flex max-w-full justify-between items-center flex-wrap md:border border-b border-gray-200"
			>
				<div class="flex w-full gap-8 sm:px-5 md:items-center md:px-0">
					<div class="relative">
						<div
							class="flex h-16 w-16 flex-none items-center justify-center overflow-hidden rounded-full bg-purple-50 font-semibold uppercase md:h-[95px] md:w-[95px]"
						>
							{{ (username && username?.split(' ')[0][0]) || ''
							}}{{ (username && username?.split(' ')[1][0]) || 'N/A' }}
						</div>
					</div>

					<div class="flex w-full flex-wrap items-center justify-between gap-x-3 gap-y-2 md:gap-0">
						<div>
							<h1 class="mb-0 pb-0 text-sm font-medium capitalize text-blue-950 md:text-lg xl:text-xl">
								{{ username || '' }}
							</h1>
							<p class="mt-0 pt-0 font-medium uppercase text-purple-500 xl:text-base">
								{{ userInfo?.role === 'admin' ? 'Adminstrator' : userInfo?.role || 'N/A' }}
							</p>
						</div>

						<nuxt-link
							:to="{
								path: '/settings/edit',

								query: {
									type: 'admin'
								}
							}"
							class="max-w-[180px] rounded-md bg-primary-2 px-6 py-2 font-medium !text-white transition hover:bg-primary-500 sm:w-full sm:py-3 sm:text-center"
						>
							Edit Profile
						</nuxt-link>
					</div>
				</div>
			</shared-card>

			<shared-card
				wrapper-class="max-w-full"
				default-class="items-start w-full gap-6 md:border md:px-14 px-5 gap-y-3 sm:py-[70px]"
			>
				<div class="">
					<h4 class="mb-5 text-xs font-medium leading-tight text-blue-950 md:hidden">Personal Details</h4>

					<div class="grid w-full flex-wrap gap-y-6 sm:grid-cols-3 md:gap-y-8">
						<div
							v-for="(result, key) in profile || []"
							:key="key"
							class="flex w-full items-center gap-x-4 capitalize odd:col-span-2"
						>
							<div>
								<lazy-tools-icon
									:dir="String(result?.iconDir)"
									:icon="String(result?.iconName)"
									path-class="stroke-purple-500"
									:height="20"
									:width="20"
								/>
							</div>

							<div class="">
								<p class="my-1 pb-0 text-xs font-light text-gray-500">
									{{ stripUnderScore(key) }}
								</p>

								<h1 class="mt-0 pt-0 text-sm font-medium text-gray-600">
									{{ result?.value }}
								</h1>
							</div>
						</div>
					</div>
				</div>
			</shared-card>
		</div>

		<settings-admin-single-profile
			:img-src="adminAvatar"
			:role="userInfo?.role === 'admin' ? 'administrator' : userInfo?.role || 'n/a'"
			:profile="profileCardInformation || null"
			class=""
		/>
	</section>
</template>
